import {
  Component,
  computed,
  DestroyRef,
  inject,
  model,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { UiDialog, UiSplitButton, SplitButtonOption } from '@shared/components/ui';
import { AgregarProductoVenta } from '@shared/components/agregar-producto-venta/agregar-producto-venta';
import {
  ActualizarEstadoProductoComandaReq,
  AgregarProductoComandaReq,
  MesaRes,
} from '@core/interfaces/request-response/comanda.dto';
import { CurrencyPipe } from '@angular/common';
import { EComandaProductoEstado } from '@core/enums/comanda.enum';
import {
  buildProductoCarritoToProductoComandaRq,
  estadoComandaMesaEnumToString,
  estadoGeneralMesaPorProductos,
  logger,
} from '@shared/utils/functions.utils';
import { IProductoCarritoData } from '@core/interfaces/entidades/productoCarrito.entidad';
import { ComandaApiService } from '@core/services/api/comanda-api.service';
import { ToastService } from '@shared/services/toast.service';
import { ModalConfirmacion } from '@shared/components/modal-confirmacion/modal-confirmacion';
import { ProcesarVenta } from '@shared/components/procesar-venta/procesar-venta';
import { CarritoCompraService } from '@shared/services/carrito-compra.service';
import { ComandaCardProducto } from './comanda-card-producto/comanda-card-producto';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { AgregarMesa } from '../agregar-mesa/agregar-mesa';

import { ImpresoraApiService } from '@core/services/api/impresora-api.service';
import { ImpresoraService } from '@shared/services/impresora.service';
import { AuthStateService } from '@shared/services/auth-state.service';
import { ETipoImpresion } from '@core/enums/impresora.enum';
import {
  CreatePrintJobRq,
  ImpresoraDatosComandaRq,
} from '@core/interfaces/request-response/impresora.dto.interface';
import { DeviceService } from '@shared/services/device.service';
import { buildPrecuentaImagenFile } from '@shared/utils/formatos_impresion_imagen.utils';
import { ID_DESCARGA_PDF } from '@shared/constants/impresora.const';
import { EmpresaService } from '@shared/services/empresa.service';
import { IEmpresaData } from '@core/interfaces/entidades/empresa.entidad';
import { buildImpresion } from '@shared/utils/formatos_impresion.utils';
import { QzService } from '@shared/services/qz.service';

@Component({
  selector: 'app-ver-comanda',
  imports: [
    UiDialog,
    UiSplitButton,
    AgregarProductoVenta,
    CurrencyPipe,
    ModalConfirmacion,
    ProcesarVenta,
    ComandaCardProducto,
    AgregarMesa,
  ],
  templateUrl: './ver-comanda.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ver-comanda.css',
})
export class VerComanda implements OnInit {
  private readonly now = signal<number>(Date.now());
  private readonly isBrowser: boolean;
  constructor() {
    this.isBrowser = this.deviceService.isBrowser();
    // tiempo
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.now.set(Date.now()));
  }
  // servicios
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly comandaApiService = inject(ComandaApiService);
  private readonly carritoCompraService = inject(CarritoCompraService);
  private readonly impresoraApiService = inject(ImpresoraApiService);
  private readonly impresoraService = inject(ImpresoraService);
  private readonly authStateService = inject(AuthStateService);
  private readonly deviceService = inject(DeviceService);
  private readonly empresaService = inject(EmpresaService);
  private readonly qzService = inject(QzService);

  // input
  mesa = model.required<MesaRes | null>();
  // datos
  modal_editar_mesa = signal(false);
  modal_agregar_producto = signal(false);
  modal_confirmar_entregar_todos = signal(false);
  modal_confirmar_limpiar_productos = signal(false);
  modal_procesar_pago = signal(false);

  impresoraSeleccionada = signal<number | null>(null);
  cerrar = output<void>();

  opcionesImpresion: SplitButtonOption[] = [
    {
      label: 'Imprimir Pendientes',
      command: () => {
        this.imprimirComanda(true);
      },
    },
    {
      label: 'Imprimir comanda',
      command: () => {
        this.imprimirComanda();
      },
    },
    {
      label: 'Imprimir precuenta',
      command: () => {
        this.imprimirPreCuenta();
      },
    },
    {
      label: 'Enviar imagen precuenta',
      command: () => {
        this.enviarPreCuentaImagen();
      },
    },
  ];

  // metodos
  estadoGeneralMesaPorProductos = computed((): string => {
    return estadoComandaMesaEnumToString(
      estadoGeneralMesaPorProductos(this.mesa()?.comanda?.productos || []),
    );
  });

  calcularPrecioTotal = computed((): number => {
    if (this.mesa() === null || this.mesa()!.comanda === null) return 0;
    const total = this.mesa()!.comanda!.productos.reduce(
      (total, producto) => total + Number(producto.subtotal),
      0,
    );
    return total;
  });

  calcularTiempoTranscurrido = computed((): string => {
    this.now();
    if (!this.mesa()?.comanda?.productos.length) return '--';

    const fechaApertura = new Date(this.mesa()?.comanda?.productos.at(0)?.created_at || '');
    const tiempoTranscurrido = this.now() - fechaApertura.getTime();
    const horasTranscurridas = Math.floor(tiempoTranscurrido / 3600000);
    const minutosTranscurridos = Math.floor((tiempoTranscurrido % 3600000) / 60000);
    const segundosTranscurridos = Math.floor((tiempoTranscurrido % 60000) / 1000);

    return `${String(horasTranscurridas).padStart(2, '0')}:${String(minutosTranscurridos).padStart(2, '0')}:${String(segundosTranscurridos).padStart(2, '0')}`;
  });

  handleAgregarProductos(productos: IProductoCarritoData[]) {
    if (this.mesa() === null) return;
    const rq: AgregarProductoComandaReq = {
      mesa_id: this.mesa()!.id,
      productos: productos.map((producto) => buildProductoCarritoToProductoComandaRq(producto)),
    };
    this.comandaApiService.agregarProductosComanda(rq).subscribe({
      next: () => {
        this.modal_agregar_producto.set(false);
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron agregar los productos a la comanda.',
        );
        console.error('Error al agregar productos a la comanda:', err);
      },
    });
  }
  // Modal
  entregarTodos() {
    if (this.mesa() === null) return;
    const id_productos =
      this.mesa()!
        .comanda?.productos.filter(
          (producto) =>
            producto.estado !== EComandaProductoEstado.ENTREGADO &&
            producto.estado !== EComandaProductoEstado.CANCELADO,
        )
        .map((producto) => producto.id) || [];
    if (id_productos.length === 0) {
      this.toastService.show(
        'top-right',
        'info',
        'Información',
        'No hay productos en la comanda para entregar.',
      );
      return;
    }
    // entregar todos esten o no en listo
    const rq: ActualizarEstadoProductoComandaReq = {
      mesa_id: this.mesa()!.id,
      comandas_items_ids: id_productos,
      estado: EComandaProductoEstado.ENTREGADO,
    };
    this.apiActualizarEstado(rq);
    this.modal_confirmar_entregar_todos.set(false);
  }
  limpiarProductos() {
    if (this.mesa() === null) return;
    if (!this.mesa()!.comanda || this.mesa()!.comanda?.productos.length === 0) {
      this.toastService.show(
        'top-right',
        'info',
        'Información',
        'No hay productos en la comanda para limpiar.',
      );
      return;
    }
    const mensaje_error_por_venta = 'No se pudo limpiar la mesa.';
    this.comandaApiService.limpiarMesa(this.mesa()!.id).subscribe({
      next: (res) => {
        if (!res.success) {
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            res.message || mensaje_error_por_venta,
          );
          return;
        }
        this.toastService.show('top-right', 'success', 'Éxito', 'Mesa limpiada correctamente.');
      },
      error: (err) => {
        console.error('Error al limpiar la mesa:', err);
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || mensaje_error_por_venta,
        );
      },
      complete: () => {
        this.modal_confirmar_limpiar_productos.set(false);
      },
    });
  }
  private apiActualizarEstado(rq: ActualizarEstadoProductoComandaReq) {
    this.comandaApiService.actualizarEstadoProductoComanda(rq).subscribe({
      next: (rs) => {
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          rs.message || 'Estado del producto actualizado correctamente.',
        );
      },
      error: (err) => {
        console.error('Error al actualizar el estado del producto:', err);
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo actualizar el estado del producto.',
        );
      },
    });
  }

  vender() {
    if (!this.mesa() || !this.mesa()?.comanda || !this.mesa()?.comanda?.productos.length) {
      this.toastService.show(
        'top-right',
        'info',
        'Procesar venta',
        'No hay productos para procesar.',
      );
      return;
    }
    this.modal_procesar_pago.set(true);
    this.carritoCompraService.limpiar();
    this.carritoCompraService.cargarProductosComandas(this.mesa()?.comanda!.productos!);
  }
  imprimirPreCuenta() {
    const impresora_id = this.impresoraSeleccionada();

    if (impresora_id === null || impresora_id === undefined) {
      return;
    }

    const datosAImprimir: ImpresoraDatosComandaRq = {
      fecha_venta: new Date(),
      productos: this.mesa()?.comanda?.productos || [],
      nombre_mesa: this.mesa()?.nombre || 'Mesa sin nombre',
    };

    if (impresora_id === ID_DESCARGA_PDF) {
      void this.descargarPdfLocal(
        ETipoImpresion.PREVENTA,
        datosAImprimir,
        `precuenta-${this.mesa()?.nombre || 'sin-nombre'}.pdf`,
      );
      return;
    }

    const rq: CreatePrintJobRq = {
      tipo_impresion: ETipoImpresion.PREVENTA,
      id_config_impresora: impresora_id,
      datos: datosAImprimir,
    };
    this.impresoraApiService.imprimir(rq).subscribe({
      next: (res) => {
        if (!res.success) {
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            res.message || 'No se pudo enviar la precuenta a imprimir.',
          );
          return;
        }
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          res.message || 'Precuenta enviada a imprimir correctamente.',
        );
      },
      error: (err) => {
        console.error('Error al imprimir la precuenta:', err);
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo enviar la precuenta a imprimir.',
        );
      },
    });
  }

  private async descargarPdfLocal(
    tipoImpresion: ETipoImpresion,
    datos: ImpresoraDatosComandaRq,
    filename: string,
  ): Promise<void> {
    const empresaData = this.empresaService.getEmpresaData();
    const empresaPlaceholder: Record<string, unknown> = {
      id: 0, ruc: '', nombre: '', direccion: null, telefono: null, logo_url: null,
      email: '', sistemas_activos: [], anuncios: null,
      maximo_comandas: 0, maximo_gestion_tiempos: 0, maximo_sucursales: 0, maximo_telefonos: 0,
    };
    const htmlContent = await buildImpresion(
      (empresaData ?? empresaPlaceholder) as IEmpresaData,
      tipoImpresion,
      datos as unknown as Record<string, unknown>,
    );
    await this.qzService.printHtmlWithBrowserDialog(htmlContent, filename, 80);
  }

  imprimirComanda(solo_pendientes: boolean = false) {
    const impresora_id = this.impresoraSeleccionada();

    if (impresora_id === null || impresora_id === undefined) {
      return;
    }

    const datosAImprimir: ImpresoraDatosComandaRq = {
      nombre_mesa: this.mesa()?.nombre || 'Mesa sin nombre',
      fecha_venta: new Date(),
      colaborador_nombre: this.authStateService.nombreCompleto() || 'Sin colaborador',
      productos: solo_pendientes
        ? this.mesa()?.comanda?.productos.filter(
            (p) => p.estado == EComandaProductoEstado.PENDIENTE,
          ) || []
        : this.mesa()?.comanda?.productos || [],
    };

    if (impresora_id === ID_DESCARGA_PDF) {
      void this.descargarPdfLocal(
        ETipoImpresion.COMANDA,
        datosAImprimir,
        `comanda-${this.mesa()?.nombre || 'sin-nombre'}.pdf`,
      );
      return;
    }

    const rq: CreatePrintJobRq = {
      tipo_impresion: ETipoImpresion.COMANDA,
      id_config_impresora: impresora_id,
      datos: datosAImprimir,
    };
    this.impresoraApiService.imprimir(rq).subscribe({
      next: (res) => {
        if (!res.success) {
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            res.message || 'No se pudo enviar la comanda a imprimir.',
          );
          return;
        }
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          res.message || 'Comanda enviada a imprimir correctamente.',
        );
      },
      error: (err) => {
        console.error('Error al imprimir la comanda:', err);
        if (err.status === 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo enviar la comanda a imprimir.',
        );
      },
    });
  }
  async enviarPreCuentaImagen() {
    if (!this.isBrowser) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Esta opción solo está disponible en navegador.',
      );
      return;
    }

    const mesa = this.mesa();
    const productos = mesa?.comanda?.productos || [];
    if (!mesa || productos.length === 0) {
      this.toastService.show(
        'top-right',
        'info',
        'Información',
        'No hay productos para compartir.',
      );
      return;
    }

    try {
      const file = await buildPrecuentaImagenFile({
        mesaNombre: mesa.nombre || 'Delivery',
        fechaVenta: new Date(),
        productos,
      });

      const fileName = file.name;

      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({
          title: 'Precuenta Delivery',
          text: `Precuenta ${mesa.nombre}`,
          files: [file],
        });
        return;
      }

      const downloadUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      this.toastService.show(
        'top-right',
        'info',
        'Descarga realizada',
        'Tu dispositivo no soporta compartir archivos directamente; se descargó la imagen.',
      );
    } catch (error: unknown) {
      const errorName = (error as { name?: string })?.name;
      if (errorName === 'AbortError') return;
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'No se pudo compartir la precuenta como imagen.',
      );
      console.error('Error al compartir imagen de precuenta:', error);
    }
  }

  ngOnInit(): void {
    this.impresoraService.impresoraSeleccionada$.subscribe((id) => {
      logger('Impresora seleccionada:', id);
      this.impresoraSeleccionada.set(id);
    });
  }
}
